import { CompleteKundali, KundaliChartData, PlanetName, PlanetPosition, ZodiacSign } from '../types/kundali';
import { ZODIAC_LORDS, ZODIAC_SIGNS, calculateLivePlanetaryEphemeris, getSavByBhava } from './vedicEngine';

export interface HouseSignification {
  sanskritName: string;
  englishTitle: string;
  categories: string[];
  karakas: string[]; // Signifier planets
  coreThemes: string[];
  description: string;
}

export const HOUSE_SIGNIFICATIONS: { [houseNum: number]: HouseSignification } = {
  1: {
    sanskritName: 'Tanu Bhava (Lagna)',
    englishTitle: 'House of Self, Vitality & Identity',
    categories: ['Kendra (Angular)', 'Trikona (Trine)', 'Dharma'],
    karakas: ['Sun (Atma)', 'Ascendant Lord'],
    coreThemes: ['Physical Body', 'Appearance', 'Vitality & Health', 'Personality', 'Life Path', 'Dignity'],
    description: 'The foundation of the entire chart representing your self-expression, health constitution, vitality, and primary orientation towards life.'
  },
  2: {
    sanskritName: 'Dhana & Kutumba Bhava',
    englishTitle: 'House of Wealth, Family & Speech',
    categories: ['Maraka (Killer / Transition)', 'Artha (Material Wealth)'],
    karakas: ['Jupiter (Wealth)', 'Mercury (Speech)', 'Venus (Family)'],
    coreThemes: ['Accumulated Wealth', 'Family Lineage', 'Speech & Voice', 'Right Eye', 'Diet & Eating habits', 'Early Childhood'],
    description: 'Governs liquid wealth, savings, banking assets, family values, truthfulness of speech, and oral nourishment.'
  },
  3: {
    sanskritName: 'Sahaja & Bhratri Bhava',
    englishTitle: 'House of Courage, Siblings & Skills',
    categories: ['Upachaya (Growth)', 'Trishadaaya', 'Kama (Desire)'],
    karakas: ['Mars (Courage & Siblings)', 'Mercury (Communication)'],
    coreThemes: ['Younger Siblings', 'Courage & Valor', 'Hand Skills & Arts', 'Writing & Media', 'Short Journeys', 'Initiative'],
    description: 'Reflects personal courage (Parakrama), fine-motor dexterity, determination, writing talents, and bonding with younger brothers and sisters.'
  },
  4: {
    sanskritName: 'Sukha & Matru Bhava',
    englishTitle: 'House of Home, Mother & Peace of Mind',
    categories: ['Kendra (Angular)', 'Moksha'],
    karakas: ['Moon (Mother & Mind)', 'Venus (Vehicles & Luxury)', 'Mars (Real Estate)'],
    coreThemes: ['Mother', 'Inner Contentment (Sukha)', 'Real Estate & Properties', 'Vehicles & Conveynaces', 'Formal Education', 'Ancestral Roots'],
    description: 'The internal sanctuary of emotional happiness, relationship with maternal figures, domestic peace, and residential properties.'
  },
  5: {
    sanskritName: 'Putra & Purva Punya Bhava',
    englishTitle: 'House of Intelligence, Children & Past Karma',
    categories: ['Trikona (Trine)', 'Dharma'],
    karakas: ['Jupiter (Children & Wisdom)', 'Mercury (Intellect)', 'Sun (Creative spark)'],
    coreThemes: ['Children & Progeny', 'Past Life Merits (Purva Punya)', 'Creative Intellect (Dhi)', 'Romance & Dating', 'Mantras & Speculation'],
    description: 'The supreme house of creative genius, intuition, high scholastic intellect, spiritual initiation (Mantra Siddhi), and auspicious ancestral karma.'
  },
  6: {
    sanskritName: 'Ari & Rina Bhava',
    englishTitle: 'House of Debts, Health Challenges & Overcoming Foes',
    categories: ['Dusthana (Difficult)', 'Upachaya (Growth)', 'Trishadaaya', 'Artha'],
    karakas: ['Mars (Defeating enemies)', 'Saturn (Daily service & Chronic issues)'],
    coreThemes: ['Overcoming Competitors', 'Acute Illnesses & Healing', 'Debts & Loans', 'Daily Employment', 'Service & Pets', 'Litigation'],
    description: 'Represents the capacity to endure hard daily labor, resolve disputes, overcome obstacles (Shatru), manage debts, and maintain metabolic immunity.'
  },
  7: {
    sanskritName: 'Kalatra & Jaya Bhava',
    englishTitle: 'House of Marriage, Partnerships & Public Relations',
    categories: ['Kendra (Angular)', 'Maraka', 'Kama'],
    karakas: ['Venus (Spouse / Love)', 'Jupiter (Husband in female chart)'],
    coreThemes: ['Spouse & Marriage', 'Business Partnerships', 'Public Interaction', 'Foreign Residence', 'Contracts & Trade', 'Social Diplomacy'],
    description: 'Reflects long-term marital union, partner qualities, legal contracts, collaborative enterprises, and public charisma.'
  },
  8: {
    sanskritName: 'Ayu & Randhra Bhava',
    englishTitle: 'House of Longevity, Secrets & Transformation',
    categories: ['Dusthana (Difficult)', 'Moksha', 'Trik (Occult)'],
    karakas: ['Saturn (Longevity / Ayush)', 'Mars (Sudden events)'],
    coreThemes: ['Longevity & Span of Life', 'Occult & Astrological Wisdom', 'Inheritance & Unearned Wealth', 'Sudden Crises & Rebirth', 'Research & Investigation'],
    description: 'The mystical domain of esoteric knowledge, psychological depth, longevity, tax/inheritance settlements, and transformative regeneration.'
  },
  9: {
    sanskritName: 'Bhagya & Dharma Bhava',
    englishTitle: 'House of Fortune, Higher Wisdom & Dharma',
    categories: ['Trikona (Trine)', 'Dharma'],
    karakas: ['Jupiter (Guru & Dharma)', 'Sun (Father & Divine grace)'],
    coreThemes: ['Divine Fortune (Bhagya)', 'Father & Preceptors (Guru)', 'Higher Philosophy & Law', 'Pilgrimages & Long Journeys', 'Morality & Righteousness'],
    description: 'The most auspicious house of fortune, divine protection, ethical orientation, higher academics, and blessings from spiritual teachers and father.'
  },
  10: {
    sanskritName: 'Karma & Rajyapaal Bhava',
    englishTitle: 'House of Career, Fame & Social Standing',
    categories: ['Kendra (Angular)', 'Upachaya (Growth)', 'Artha'],
    karakas: ['Sun (Authority)', 'Mercury (Commerce)', 'Jupiter (Prestige)', 'Saturn (Profession)'],
    coreThemes: ['Career & Vocation', 'Public Fame & Respect', 'Government & Leadership', 'Executive Achievements', 'Father (in some traditions)', 'Societal Duty'],
    description: 'The peak of the sky (Zenith/Midheaven) signifying professional prominence, career accomplishments, social authority, and public reputation.'
  },
  11: {
    sanskritName: 'Labha & Aaya Bhava',
    englishTitle: 'House of Gains, Income & Fulfillment of Desires',
    categories: ['Upachaya (Growth)', 'Trishadaaya', 'Kama'],
    karakas: ['Jupiter (Gains & Abundance)'],
    coreThemes: ['Financial Gains & Profits', 'Elder Siblings', 'Influential Friendships', 'Fulfillment of Aspirations', 'Social Networks', 'Awards & Recognition'],
    description: 'Governs steady revenue flow, fulfillment of lifelong wishes, monetization of career efforts, professional networks, and elder siblings.'
  },
  12: {
    sanskritName: 'Vyaya & Moksha Bhava',
    englishTitle: 'House of Expenditure, Foreign Lands & Liberation',
    categories: ['Dusthana (Difficult)', 'Moksha'],
    karakas: ['Saturn (Losses & Solitude)', 'Ketu (Spiritual Liberation / Moksha)', 'Venus (Bed comforts)'],
    coreThemes: ['Spiritual Liberation (Moksha)', 'Foreign Settlement & Travel', 'Expenditures & Investments', 'Meditation & Solitude', 'Sleep & Subconscious Dreams', 'Hospitalization / Seclusion'],
    description: 'The transcendental gateway to spiritual freedom, detachment, travel or residence across distant oceans, sub-conscious exploration, and charitable giving.'
  }
};

/**
 * Functional Benefic/Malefic evaluation based on Ascendant (Lagna) sign index (0=Aries .. 11=Pisces)
 */
export const getFunctionalNature = (
  planet: PlanetName,
  ascendantIndex: number
): { isFunctionalBenefic: boolean; isFunctionalMalefic: boolean; isYogakaraka: boolean; roleDescription: string } => {
  if (planet === 'Ascendant') {
    return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Lagna Point (Foundation of Self)' };
  }

  // Functional rules by Lagna
  const lagna = ascendantIndex % 12;

  switch (lagna) {
    case 0: // Aries (Mars)
      if (['Sun', 'Jupiter', 'Mars'].includes(planet)) {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: `Functional Benefic (Lord of ${planet === 'Sun' ? '5th' : planet === 'Jupiter' ? '9th/12th' : '1st/8th'} house)` };
      }
      if (['Mercury', 'Venus'].includes(planet)) {
        return { isFunctionalBenefic: false, isFunctionalMalefic: true, isYogakaraka: false, roleDescription: `Functional Malefic (Lord of ${planet === 'Mercury' ? '3rd/6th' : '2nd/7th Maraka'} house)` };
      }
      if (planet === 'Saturn') {
        return { isFunctionalBenefic: false, isFunctionalMalefic: true, isYogakaraka: false, roleDescription: 'Functional Malefic (Lord of 10th/11th house)' };
      }
      return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Neutral / Benefic (Lord of 4th house)' };

    case 1: // Taurus (Venus)
      if (planet === 'Saturn') {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: true, roleDescription: 'Supreme Yogakaraka (Lord of 9th Trikona & 10th Kendra)' };
      }
      if (['Mercury', 'Sun'].includes(planet)) {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: `Functional Benefic (Lord of ${planet === 'Mercury' ? '2nd/5th' : '4th'} house)` };
      }
      if (['Jupiter', 'Mars', 'Moon'].includes(planet)) {
        return { isFunctionalBenefic: false, isFunctionalMalefic: true, isYogakaraka: false, roleDescription: `Functional Malefic (Lord of ${planet === 'Jupiter' ? '8th/11th' : planet === 'Mars' ? '7th/12th' : '3rd'} house)` };
      }
      return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Lagna Lord (Benefic)' };

    case 2: // Gemini (Mercury)
      if (planet === 'Venus') {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Functional Benefic (Lord of 5th Trikona & 12th)' };
      }
      if (planet === 'Mercury') {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Lagna & 4th Lord (Functional Benefic)' };
      }
      if (['Mars', 'Jupiter', 'Sun'].includes(planet)) {
        return { isFunctionalBenefic: false, isFunctionalMalefic: true, isYogakaraka: false, roleDescription: `Functional Malefic (Lord of ${planet === 'Mars' ? '6th/11th' : planet === 'Jupiter' ? '7th/10th' : '3rd'} house)` };
      }
      return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Neutral / Benefic' };

    case 3: // Cancer (Moon)
      if (planet === 'Mars') {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: true, roleDescription: 'Supreme Yogakaraka (Lord of 5th Trikona & 10th Kendra)' };
      }
      if (['Jupiter', 'Moon'].includes(planet)) {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: `Functional Benefic (Lord of ${planet === 'Jupiter' ? '9th Dharma' : 'Lagna'})` };
      }
      if (['Mercury', 'Venus', 'Saturn'].includes(planet)) {
        return { isFunctionalBenefic: false, isFunctionalMalefic: true, isYogakaraka: false, roleDescription: `Functional Malefic (Lord of ${planet === 'Mercury' ? '3rd/12th' : planet === 'Venus' ? '4th/11th' : '7th/8th'} house)` };
      }
      return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Neutral / Benefic (Sun 2nd Lord)' };

    case 4: // Leo (Sun)
      if (planet === 'Mars') {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: true, roleDescription: 'Supreme Yogakaraka (Lord of 4th Kendra & 9th Trikona)' };
      }
      if (['Jupiter', 'Sun'].includes(planet)) {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: `Functional Benefic (Lord of ${planet === 'Jupiter' ? '5th Trikona' : 'Lagna'})` };
      }
      if (['Venus', 'Saturn', 'Mercury'].includes(planet)) {
        return { isFunctionalBenefic: false, isFunctionalMalefic: true, isYogakaraka: false, roleDescription: `Functional Malefic (Lord of ${planet === 'Venus' ? '3rd/10th' : planet === 'Saturn' ? '6th/7th' : '2nd/11th'} house)` };
      }
      return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Neutral (Moon 12th Lord)' };

    case 5: // Virgo (Mercury)
      if (['Venus', 'Mercury'].includes(planet)) {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: `Functional Benefic (Lord of ${planet === 'Venus' ? '2nd/9th' : '1st/10th'} house)` };
      }
      if (['Mars', 'Jupiter', 'Moon'].includes(planet)) {
        return { isFunctionalBenefic: false, isFunctionalMalefic: true, isYogakaraka: false, roleDescription: `Functional Malefic (Lord of ${planet === 'Mars' ? '3rd/8th' : planet === 'Jupiter' ? '4th/7th' : '11th'} house)` };
      }
      return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Neutral / Benefic (Saturn 5th/6th Lord)' };

    case 6: // Libra (Venus)
      if (planet === 'Saturn') {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: true, roleDescription: 'Supreme Yogakaraka (Lord of 4th Kendra & 5th Trikona)' };
      }
      if (['Mercury', 'Venus'].includes(planet)) {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: `Functional Benefic (Lord of ${planet === 'Mercury' ? '9th Dharma' : 'Lagna'})` };
      }
      if (['Jupiter', 'Sun', 'Mars'].includes(planet)) {
        return { isFunctionalBenefic: false, isFunctionalMalefic: true, isYogakaraka: false, roleDescription: `Functional Malefic (Lord of ${planet === 'Jupiter' ? '3rd/6th' : planet === 'Sun' ? '11th' : '2nd/7th'} house)` };
      }
      return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Neutral (Moon 10th Lord)' };

    case 7: // Scorpio (Mars)
      if (['Jupiter', 'Moon', 'Sun', 'Mars'].includes(planet)) {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: `Functional Benefic (Lord of ${planet === 'Jupiter' ? '5th' : planet === 'Moon' ? '9th' : planet === 'Sun' ? '10th' : 'Lagna'})` };
      }
      if (['Mercury', 'Venus'].includes(planet)) {
        return { isFunctionalBenefic: false, isFunctionalMalefic: true, isYogakaraka: false, roleDescription: `Functional Malefic (Lord of ${planet === 'Mercury' ? '8th/11th' : '7th/12th'} house)` };
      }
      return { isFunctionalBenefic: false, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Neutral (Saturn 3rd/4th Lord)' };

    case 8: // Sagittarius (Jupiter)
      if (['Sun', 'Mars', 'Jupiter'].includes(planet)) {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: `Functional Benefic (Lord of ${planet === 'Sun' ? '9th' : planet === 'Mars' ? '5th' : 'Lagna'})` };
      }
      if (['Venus', 'Mercury'].includes(planet)) {
        return { isFunctionalBenefic: false, isFunctionalMalefic: true, isYogakaraka: false, roleDescription: `Functional Malefic (Lord of ${planet === 'Venus' ? '6th/11th' : '7th/10th'} house)` };
      }
      return { isFunctionalBenefic: false, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Neutral (Moon/Saturn)' };

    case 9: // Capricorn (Saturn)
      if (planet === 'Venus') {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: true, roleDescription: 'Supreme Yogakaraka (Lord of 5th Trikona & 10th Kendra)' };
      }
      if (['Mercury', 'Saturn'].includes(planet)) {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: `Functional Benefic (Lord of ${planet === 'Mercury' ? '9th Dharma' : 'Lagna'})` };
      }
      if (['Mars', 'Jupiter', 'Moon'].includes(planet)) {
        return { isFunctionalBenefic: false, isFunctionalMalefic: true, isYogakaraka: false, roleDescription: `Functional Malefic (Lord of ${planet === 'Mars' ? '4th/11th' : planet === 'Jupiter' ? '3rd/12th' : '7th Maraka'} house)` };
      }
      return { isFunctionalBenefic: false, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Neutral (Sun 8th Lord)' };

    case 10: // Aquarius (Saturn)
      if (planet === 'Venus') {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: true, roleDescription: 'Supreme Yogakaraka (Lord of 4th Kendra & 9th Trikona)' };
      }
      if (['Saturn', 'Mercury'].includes(planet)) {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: `Functional Benefic (Lord of ${planet === 'Saturn' ? 'Lagna' : '5th Trikona'})` };
      }
      if (['Jupiter', 'Moon', 'Mars', 'Sun'].includes(planet)) {
        return { isFunctionalBenefic: false, isFunctionalMalefic: true, isYogakaraka: false, roleDescription: `Functional Malefic (Lord of ${planet === 'Jupiter' ? '2nd/11th' : planet === 'Moon' ? '6th' : planet === 'Mars' ? '3rd/10th' : '7th Maraka'} house)` };
      }
      return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Neutral' };

    case 11: // Pisces (Jupiter)
      if (['Moon', 'Mars', 'Jupiter'].includes(planet)) {
        return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: `Functional Benefic (Lord of ${planet === 'Moon' ? '5th' : planet === 'Mars' ? '9th' : 'Lagna'})` };
      }
      if (['Venus', 'Sun', 'Mercury', 'Saturn'].includes(planet)) {
        return { isFunctionalBenefic: false, isFunctionalMalefic: true, isYogakaraka: false, roleDescription: `Functional Malefic (Lord of ${planet === 'Venus' ? '3rd/8th' : planet === 'Sun' ? '6th' : planet === 'Mercury' ? '4th/7th' : '11th/12th'} house)` };
      }
      return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Benefic' };

    default:
      return { isFunctionalBenefic: true, isFunctionalMalefic: false, isYogakaraka: false, roleDescription: 'Planetary influence' };
  }
};

export interface AspectDetail {
  planet: PlanetName;
  aspectType: string;
  isBenefic: boolean;
  dignity: string;
  sourceHouse: number;
}

/**
 * Calculates authentic Vedic Drishti (Aspects) on a target house
 */
export const getAspectsOnHouse = (
  targetHouse: number,
  planets: PlanetPosition[],
  ascendantIndex: number
): AspectDetail[] => {
  const aspects: AspectDetail[] = [];

  planets.forEach((p) => {
    if (p.name === 'Ascendant') return;
    const sourceHouse = p.house;
    if (sourceHouse === targetHouse) return; // Occupant, not aspecting

    // Calculate relative forward distance (1 to 12)
    const dist = ((targetHouse - sourceHouse + 12) % 12) + 1;
    const { isFunctionalBenefic } = getFunctionalNature(p.name, ascendantIndex);
    const isNaturalBenefic = ['Jupiter', 'Venus', 'Moon', 'Mercury'].includes(p.name);
    const isBenefic = isFunctionalBenefic || isNaturalBenefic;

    // 1. All planets cast 7th house aspect (dist === 7)
    if (dist === 7) {
      aspects.push({
        planet: p.name,
        aspectType: '7th Full Aspect (Direct Drishti)',
        isBenefic,
        dignity: p.dignity,
        sourceHouse
      });
    }

    // 2. Mars special aspects: 4th and 8th
    if (p.name === 'Mars') {
      if (dist === 4) {
        aspects.push({
          planet: p.name,
          aspectType: '4th Special Chaturtha Drishti',
          isBenefic: false,
          dignity: p.dignity,
          sourceHouse
        });
      } else if (dist === 8) {
        aspects.push({
          planet: p.name,
          aspectType: '8th Special Ashta Drishti',
          isBenefic: false,
          dignity: p.dignity,
          sourceHouse
        });
      }
    }

    // 3. Jupiter special aspects: 5th and 9th
    if (['Jupiter'].includes(p.name)) {
      if (dist === 5) {
        aspects.push({
          planet: p.name,
          aspectType: '5th Special Trikona Drishti',
          isBenefic: true,
          dignity: p.dignity,
          sourceHouse
        });
      } else if (dist === 9) {
        aspects.push({
          planet: p.name,
          aspectType: '9th Special Bhagya Drishti',
          isBenefic: true,
          dignity: p.dignity,
          sourceHouse
        });
      }
    }

    // 4. Saturn special aspects: 3rd and 10th
    if (p.name === 'Saturn') {
      if (dist === 3) {
        aspects.push({
          planet: p.name,
          aspectType: '3rd Special Tritiya Drishti',
          isBenefic: false,
          dignity: p.dignity,
          sourceHouse
        });
      } else if (dist === 10) {
        aspects.push({
          planet: p.name,
          aspectType: '10th Special Dashama Drishti',
          isBenefic: false,
          dignity: p.dignity,
          sourceHouse
        });
      }
    }
  });

  return aspects;
};

export interface ParivartanaInfo {
  type: 'Maha Yoga' | 'Dainya Yoga' | 'Khala Yoga';
  partnerHouse: number;
  partnerLord: string;
  description: string;
  isAuspicious: boolean;
}

/**
 * Checks for Parivartana Yoga (Exchange of House Lords)
 */
export const detectParivartana = (
  houseNum: number,
  chartData: KundaliChartData
): ParivartanaInfo | null => {
  const currentHouse = chartData.houses.find((h) => h.houseNumber === houseNum);
  if (!currentHouse) return null;

  const currentLordName = currentHouse.signLord;
  const currentLordPos = chartData.planets.find((p) => p.name === currentLordName);
  if (!currentLordPos) return null;

  const targetHouseNum = currentLordPos.house;
  if (targetHouseNum === houseNum) return null; // In own house, not exchange

  const targetHouse = chartData.houses.find((h) => h.houseNumber === targetHouseNum);
  if (!targetHouse) return null;

  const targetLordName = targetHouse.signLord;
  const targetLordPos = chartData.planets.find((p) => p.name === targetLordName);
  if (!targetLordPos) return null;

  // Verify reciprocal exchange
  if (targetLordPos.house === houseNum) {
    const isDusthana = (h: number) => [6, 8, 12].includes(h);
    const isKhala = (h: number) => h === 3;

    if (isDusthana(houseNum) || isDusthana(targetHouseNum)) {
      return {
        type: 'Dainya Yoga',
        partnerHouse: targetHouseNum,
        partnerLord: targetLordName,
        isAuspicious: false,
        description: `Dainya Yoga: Lord of House ${houseNum} (${currentLordName}) exchanges sign with Lord of House ${targetHouseNum} (${targetLordName}). Involves difficult houses; signifies challenges turning into deep spiritual or transformative growth.`
      };
    } else if (isKhala(houseNum) || isKhala(targetHouseNum)) {
      return {
        type: 'Khala Yoga',
        partnerHouse: targetHouseNum,
        partnerLord: targetLordName,
        isAuspicious: false,
        description: `Khala Yoga: Lord of House ${houseNum} (${currentLordName}) exchanges sign with Lord of House ${targetHouseNum} (${targetLordName}). Fluctuating fortunes; victory achieved through personal perseverance and grit.`
      };
    } else {
      return {
        type: 'Maha Yoga',
        partnerHouse: targetHouseNum,
        partnerLord: targetLordName,
        isAuspicious: true,
        description: `Maha Yoga: Auspicious exchange between Lord of House ${houseNum} (${currentLordName}) and Lord of House ${targetHouseNum} (${targetLordName}). Grants strong dignity, power, wealth, and enduring protection to both houses.`
      };
    }
  }

  return null;
};

export interface AshtakvargaHouseEvaluation {
  points: number | null;
  benchmark: number; // 28 is the astrological baseline average
  rating: 'Exceptional (32+)' | 'Strong & Auspicious (28-31)' | 'Moderate (25-27)' | 'Challenging (<25)';
  statusColor: 'emerald' | 'teal' | 'amber' | 'rose';
  analysis: string;
  transitImplication: string;
  bhinnaContributors: {
    planet: PlanetName;
    bindus: number;
    status: 'Strong' | 'Average' | 'Low';
  }[];
  strongestGrahas: string[];
  weakestGrahas: string[];
}

export interface HouseGocharTransitOccupant {
  planet: PlanetName;
  degreeInSign: number;
  formattedDegree: string;
  isRetrograde: boolean;
  transitSign: ZodiacSign;
  houseFromLagna: number;
  houseFromMoon: number;
  isBeneficFromMoon: boolean;
  bhinnaBindus: number;
  vedicImpact: string;
}

export interface HouseGocharTransitAspect {
  planet: PlanetName;
  aspectType: string;
  fromHouse: number;
  fromSign: ZodiacSign;
  isBenefic: boolean;
  impact: string;
}

export interface HouseGocharEvaluation {
  calculatedAt: string;
  occupants: HouseGocharTransitOccupant[];
  aspects: HouseGocharTransitAspect[];
  overallClimate: 'Supreme Auspicious' | 'Favorable & Productive' | 'Dynamic & Mixed' | 'Vigilant / Transformative';
  climateColor: 'emerald' | 'teal' | 'amber' | 'rose';
  analysisVerdict: string;
  transitActionGuidance: string;
  ashtakvargaSynergy: string;
}

export interface YogaInfo {
  name: string;
  type: string;
  description: string;
  planets: string[];
  isAuspicious: boolean;
}

export interface ComprehensiveHouseAnalysis {
  houseNumber: number;
  sign: ZodiacSign;
  signIndex: number;
  signLord: string;
  signLordPosition: PlanetPosition | undefined;
  sarvashtakvargaPoints: number | null;
  ashtakvargaEvaluation: AshtakvargaHouseEvaluation;
  gocharEvaluation: HouseGocharEvaluation;
  signification: HouseSignification;
  occupants: (PlanetPosition & {
    isNaturalBenefic: boolean;
    isFunctionalBenefic: boolean;
    isYogakaraka: boolean;
    roleDescription: string;
  })[];
  aspects: AspectDetail[];
  parivartana: ParivartanaInfo | null;
  yogas: YogaInfo[];
  houseStrengthVerdict: {
    badge: string;
    color: 'emerald' | 'amber' | 'rose' | 'teal' | 'indigo';
    summary: string;
  };
}


/**
 * Detects general yogas active for a specific house
 */
function detectHouseYogas(houseNum: number, chartData: KundaliChartData): YogaInfo[] {
  const yogas: YogaInfo[] = [];
  const planets = chartData.planets;
  const jupiter = planets.find(p => p.name === 'Jupiter');
  const moon = planets.find(p => p.name === 'Moon');
  
  // 1. Gaja Kesari Yoga (Jupiter in Kendra from Moon)
  if (jupiter && moon) {
    const dist = (jupiter.house - moon.house + 12) % 12;
    const isKendra = [0, 3, 6, 9].includes(dist);
    if (isKendra && jupiter.house === houseNum) {
      yogas.push({
        name: 'Gaja Kesari Yoga',
        type: 'Dhana/Vidya Yoga',
        description: 'Jupiter is in a Kendra (1, 4, 7, 10) position from the Moon. This brings great wisdom, wealth, lasting fame, and a virtuous character. The native is like a lion (Kesari) among elephants (Gaja).',
        planets: ['Jupiter', 'Moon'],
        isAuspicious: true
      });
    }
  }

  // 2. Lakshmi Yoga (9th Lord in Kendra and Lagna Lord strong)
  const safeAscIndex = chartData?.ascendantIndex ?? 0;
  const lagnaLordName = ZODIAC_LORDS[ZODIAC_SIGNS[safeAscIndex]];
  const ninthLordName = ZODIAC_LORDS[ZODIAC_SIGNS[(safeAscIndex + 8) % 12]];
  const ninthLord = planets.find(p => p.name === ninthLordName);
  const lagnaLord = planets.find(p => p.name === lagnaLordName);
  
  if (ninthLord && lagnaLord && ninthLord.house === houseNum) {
    const isNinthLordInKendra = [1, 4, 7, 10].includes(ninthLord.house);
    const isLagnaLordStrong = ['Exalted', 'Own House', 'Moolatrikona'].includes(lagnaLord.dignity as string);
    if (isNinthLordInKendra && isLagnaLordStrong) {
      yogas.push({
        name: 'Lakshmi Yoga',
        type: 'Wealth Yoga',
        description: 'The Lord of the 9th house is in a Kendra while the Lagna Lord is powerful. This confers immense wealth, prosperity, and an attractive personality.',
        planets: [ninthLordName, lagnaLordName],
        isAuspicious: true
      });
    }
  }

  return yogas;
}

/**
 * Evaluates live Gochar (transits) dynamically for a specific house
 */
export function evaluateHouseGocharCondition(
  houseNumber: number,
  kundali: CompleteKundali,
  transitDate: Date = new Date()
): HouseGocharEvaluation {
  const natalAsc = kundali.planets.find((p) => p.name === 'Ascendant') || { signIndex: 0 };
  const natalMoon = kundali.planets.find((p) => p.name === 'Moon') || { signIndex: 0 };
  const ascSignIdx = natalAsc.signIndex;
  const moonSignIdx = natalMoon.signIndex;

  // Live ephemeris calculated right now
  const liveEphemeris = calculateLivePlanetaryEphemeris(transitDate);

  const BENEFIC_HOUSES_FROM_MOON: { [key in PlanetName]?: number[] } = {
    Sun: [3, 6, 10, 11],
    Moon: [1, 3, 6, 7, 10, 11],
    Mars: [3, 6, 11],
    Mercury: [2, 4, 6, 8, 10, 11],
    Jupiter: [2, 5, 7, 9, 11],
    Venus: [1, 2, 3, 4, 5, 8, 9, 11, 12],
    Saturn: [3, 6, 11],
    Rahu: [3, 6, 11],
    Ketu: [3, 6, 11]
  };

  const naturalBenefics: PlanetName[] = ['Jupiter', 'Venus', 'Moon', 'Mercury'];

  const occupants: HouseGocharTransitOccupant[] = [];
  const aspects: HouseGocharTransitAspect[] = [];

  liveEphemeris.forEach((tp) => {
    const houseFromLagna = ((tp.signIndex - ascSignIdx + 12) % 12) + 1;
    const houseFromMoon = ((tp.signIndex - moonSignIdx + 12) % 12) + 1;
    const bhinnaBindus = kundali.ashtakvarga?.bhinna?.[tp.planet]?.[houseFromLagna - 1] ?? 4;
    const isBeneficFromMoon = (BENEFIC_HOUSES_FROM_MOON[tp.planet] || [3, 6, 11]).includes(houseFromMoon);

    // Is this planet transiting inside this house?
    if (houseFromLagna === houseNumber) {
      let vedicImpact = '';
      if (tp.planet === 'Jupiter') {
        vedicImpact = `Guru Gochar directly activates House ${houseNumber}. Expands auspicious wisdom, divine grace, and material fruition (${bhinnaBindus} BAV bindus).`;
      } else if (tp.planet === 'Saturn') {
        if ([1, 2, 12].includes(houseFromMoon)) {
          vedicImpact = `Shani transits here under Sade Sati period (House ${houseFromMoon} from Moon). Requires disciplined persistence and mature handling.`;
        } else if ([4, 8].includes(houseFromMoon)) {
          vedicImpact = `Shani transits here under Dhaiya / Kantaka Shani. Demands steady focus and patience with delays.`;
        } else {
          vedicImpact = `Shani transit brings foundational structuring, perseverance, and karmic refinement (${bhinnaBindus} BAV bindus).`;
        }
      } else if (tp.planet === 'Rahu') {
        vedicImpact = `Rahu transit triggers intense focus, desire for sudden breakthroughs, foreign connections, and unconventional innovation.`;
      } else if (tp.planet === 'Ketu') {
        vedicImpact = `Ketu transit brings intuitive insights, spiritual detachment, analytical precision, and karmic release.`;
      } else if (tp.planet === 'Sun') {
        vedicImpact = `Surya transit illuminates house affairs with authority, focus, and vitality (${tp.formattedDegree}).`;
      } else if (tp.planet === 'Mars') {
        vedicImpact = `Mangal transit fuels high drive, physical initiative, courage, and quick decisions (${tp.formattedDegree}).`;
      } else if (tp.planet === 'Venus') {
        vedicImpact = `Shukra transit enhances diplomatic charm, creative aesthetics, relational harmony, and comfort.`;
      } else if (tp.planet === 'Mercury') {
        vedicImpact = `Budha transit sharpens intellectual agility, business commerce, trade, and communication.`;
      } else if (tp.planet === 'Moon') {
        vedicImpact = `Chandra transit activates short-term emotional tides, domestic receptivity, and intuition.`;
      }

      occupants.push({
        planet: tp.planet,
        degreeInSign: tp.degreeInSign,
        formattedDegree: tp.formattedDegree,
        isRetrograde: tp.isRetrograde,
        transitSign: tp.sign,
        houseFromLagna,
        houseFromMoon,
        isBeneficFromMoon,
        bhinnaBindus,
        vedicImpact
      });
    }

    // Check aspects cast onto houseNumber
    const checkAndAddAspect = (aspectOffset: number, aspectLabel: string) => {
      const targetHouse = ((houseFromLagna + aspectOffset - 1) % 12) + 1;
      if (targetHouse === houseNumber) {
        const isBenefic = naturalBenefics.includes(tp.planet);
        let impact = '';
        if (tp.planet === 'Jupiter') {
          impact = `Transiting Guru casts divine ${aspectLabel} onto House ${houseNumber}, conferring protection, virtue, and mitigating negativities.`;
        } else if (tp.planet === 'Saturn') {
          impact = `Transiting Shani casts ${aspectLabel} onto House ${houseNumber}, instilling discipline, realistic pacing, and karmic testing.`;
        } else if (tp.planet === 'Mars') {
          impact = `Transiting Mangal casts energetic ${aspectLabel} onto House ${houseNumber}, injecting dynamic drive and assertiveness.`;
        } else if (tp.planet === 'Rahu' || tp.planet === 'Ketu') {
          impact = `Transiting ${tp.planet} casts ${aspectLabel} onto House ${houseNumber}, triggering unconventional developments.`;
        } else {
          impact = `Transiting ${tp.planet} casts full 7th Drishti onto House ${houseNumber}, illuminating house affairs.`;
        }

        aspects.push({
          planet: tp.planet,
          aspectType: aspectLabel,
          fromHouse: houseFromLagna,
          fromSign: tp.sign,
          isBenefic,
          impact
        });
      }
    };

    // Sun, Moon, Mercury, Venus aspect 7th house
    if (['Sun', 'Moon', 'Mercury', 'Venus'].includes(tp.planet)) {
      checkAndAddAspect(6, '7th Full Drishti');
    }
    // Mars aspects 4th, 7th, 8th
    if (tp.planet === 'Mars') {
      checkAndAddAspect(3, '4th Special Drishti');
      checkAndAddAspect(6, '7th Direct Drishti');
      checkAndAddAspect(7, '8th Special Drishti');
    }
    // Jupiter aspects 5th, 7th, 9th
    if (tp.planet === 'Jupiter') {
      checkAndAddAspect(4, '5th Trine Drishti (Guru Kripa)');
      checkAndAddAspect(6, '7th Direct Drishti');
      checkAndAddAspect(8, '9th Trine Drishti (Guru Kripa)');
    }
    // Saturn aspects 3rd, 7th, 10th
    if (tp.planet === 'Saturn') {
      checkAndAddAspect(2, '3rd Special Drishti');
      checkAndAddAspect(6, '7th Direct Drishti');
      checkAndAddAspect(9, '10th Special Drishti');
    }
    // Rahu & Ketu aspect 7th
    if (tp.planet === 'Rahu' || tp.planet === 'Ketu') {
      checkAndAddAspect(6, '7th Direct Drishti');
    }
  });

  // Calculate Overall Climate & Verdict
  const savPoints = kundali.ashtakvarga ? getSavByBhava(kundali.ashtakvarga, houseNumber, kundali.houses) : 28;
  const hasJupiterSupport = occupants.some((o) => o.planet === 'Jupiter') || aspects.some((a) => a.planet === 'Jupiter');
  const hasSaturnOccupant = occupants.some((o) => o.planet === 'Saturn');
  const hasRahuOccupant = occupants.some((o) => o.planet === 'Rahu');

  let overallClimate: HouseGocharEvaluation['overallClimate'] = 'Favorable & Productive';
  let climateColor: HouseGocharEvaluation['climateColor'] = 'teal';
  let analysisVerdict = '';
  let transitActionGuidance = '';

  if (hasJupiterSupport && savPoints >= 28) {
    overallClimate = 'Supreme Auspicious';
    climateColor = 'emerald';
    analysisVerdict = `House ${houseNumber} receives auspicious Jupiter transit blessings combined with strong Ashtakavarga support (${savPoints} pts). Activities related to this house blossom with ease and bring enduring recognition.`;
    transitActionGuidance = `Optimal time to initiate major ventures, sign important contracts, expand projects, and leverage current opportunities in this domain.`;
  } else if (hasSaturnOccupant && savPoints < 28) {
    overallClimate = 'Vigilant / Transformative';
    climateColor = 'rose';
    analysisVerdict = `Saturn's active transit across House ${houseNumber} paired with moderate/lower SAV strength (${savPoints} pts) demands patience, meticulous execution, and caution against haste.`;
    transitActionGuidance = `Adopt structured routines, verify legal and financial details, practice patience with authority or family members, and chant Shani mantras.`;
  } else if (hasRahuOccupant) {
    overallClimate = 'Dynamic & Mixed';
    climateColor = 'amber';
    analysisVerdict = `Rahu's presence in House ${houseNumber} sparks ambition and unconventional opportunities, but also invites illusions or impatience.`;
    transitActionGuidance = `Harness innovative ideas while grounding yourself in verified facts; avoid impulsive speculations or overcommitting.`;
  } else if (occupants.length > 0) {
    const occNames = occupants.map((o) => o.planet).join(', ');
    overallClimate = 'Favorable & Productive';
    climateColor = 'teal';
    analysisVerdict = `Active planetary presence (${occNames}) is dynamically stimulating House ${houseNumber}, bringing focused energy and engagement to its core themes.`;
    transitActionGuidance = `Stay engaged with proactive initiatives, maintain steady effort, and capitalize on planetary momentum.`;
  } else if (aspects.length > 0) {
    const aspNames = aspects.map((a) => a.planet).join(', ');
    overallClimate = 'Favorable & Productive';
    climateColor = 'teal';
    analysisVerdict = `House ${houseNumber} is currently receiving dynamic transit aspects from ${aspNames}, providing steady background influence and subtle evolutionary momentum.`;
    transitActionGuidance = `Align with the harmonious qualities of aspecting benefics while maintaining steady discipline in daily routines.`;
  } else {
    overallClimate = 'Favorable & Productive';
    climateColor = 'teal';
    analysisVerdict = `House ${houseNumber} operates in a serene transit window, buffered by ${savPoints} Sarvashtakvarga points without severe direct malefic interference.`;
    transitActionGuidance = `Favorable period for steady progress, consolidating gains, and maintaining consistent long-term habits.`;
  }

  const ashtakvargaSynergy =
    savPoints >= 30
      ? `High SAV score (${savPoints} pts) provides a buoyant karmic foundation. Transiting planets produce their highest auspicious potential with minimal friction.`
      : savPoints >= 26
      ? `Balanced SAV score (${savPoints} pts). Transits respond proportionately to conscious effort and structured planning.`
      : `Sensitive SAV score (${savPoints} pts). Transiting planets require extra conscious care, patience, and remedial alignment to yield optimal results.`;

  return {
    calculatedAt: new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(transitDate),
    occupants,
    aspects,
    overallClimate,
    climateColor,
    analysisVerdict,
    transitActionGuidance,
    ashtakvargaSynergy
  };
}


/**
 * Main House Inspector Comprehensive Evaluation
 */
export const getComprehensiveHouseCondition = (
  houseNumber: number,
  chartData: KundaliChartData,
  kundali: CompleteKundali,
  transitDate: Date = new Date()
): ComprehensiveHouseAnalysis | null => {
  const house = chartData?.houses?.find((h) => h.houseNumber === houseNumber);
  if (!house) return null;

  const ascSignIndex = chartData?.ascendantIndex ?? 0;
  const signLord = house.signLord;
  const signLordPosition = chartData.planets.find((p) => p.name === signLord);
  const savPoints = kundali.ashtakvarga ? getSavByBhava(kundali.ashtakvarga, houseNumber, kundali.houses) : null;

  const signification = HOUSE_SIGNIFICATIONS[houseNumber] || {
    sanskritName: `House ${houseNumber}`,
    englishTitle: `House ${houseNumber}`,
    categories: [],
    karakas: [],
    coreThemes: [],
    description: ''
  };

  // Occupants with full benefic / malefic role
  const rawOccupants = chartData.planets.filter((p) => p.house === houseNumber && p.name !== 'Ascendant');
  const occupants = rawOccupants.map((p) => {
    const naturalBenefics: PlanetName[] = ['Jupiter', 'Venus', 'Moon', 'Mercury'];
    const isNaturalBenefic = naturalBenefics.includes(p.name);
    const { isFunctionalBenefic, isYogakaraka, roleDescription } = getFunctionalNature(p.name, ascSignIndex);

    return {
      ...p,
      isNaturalBenefic,
      isFunctionalBenefic,
      isYogakaraka,
      roleDescription
    };
  });

  // Aspects on this house
  const aspects = getAspectsOnHouse(houseNumber, chartData.planets, ascSignIndex);

  // Parivartana Yoga detection
  const parivartana = detectParivartana(houseNumber, chartData);

  // General Yoga detection
  const yogas = detectHouseYogas(houseNumber, chartData);

  // Score House Strength
  let score = 0;

  // 1. Lord strength
  if (signLordPosition) {
    if (signLordPosition.dignity === 'Exalted') score += 3;
    else if (signLordPosition.dignity === 'Own House') score += 2.5;
    else if (signLordPosition.dignity === 'Debilitated') score -= 2.5;

    if (signLordPosition.isCombust) score -= 1;
    if (signLordPosition.isRetrograde && signLordPosition.dignity !== 'Debilitated') score += 0.5;

    // Lord placement in Kendra/Trikona vs Dusthana
    if ([1, 4, 7, 10, 5, 9, 11].includes(signLordPosition.house)) score += 1.5;
    else if ([6, 8, 12].includes(signLordPosition.house) && ![6, 8, 12].includes(houseNumber)) score -= 1.5;
  }

  // 2. Occupants
  occupants.forEach((p) => {
    if (p.isYogakaraka) score += 2.5;
    else if (p.isFunctionalBenefic) score += 1.5;
    else if (p.isNaturalBenefic) score += 1.5;
    else if ([3, 6, 10, 11].includes(houseNumber) && !p.isNaturalBenefic) score += 1.2; // Shatru-hanta / Upachaya valor
    else score -= 1.2;

    if (p.dignity === 'Exalted') score += 1.5;
    else if (p.dignity === 'Own House') score += 1.2;
    else if (p.dignity === 'Debilitated') score -= 1.5;
    if (p.isCombust) score -= 1;
  });

  // 3. Aspects
  aspects.forEach((a) => {
    if (a.isBenefic) score += 1.2;
    else score -= 1.0;
  });

  // 4. Parivartana
  if (parivartana?.isAuspicious) score += 2.5;

  // 5. Ashtakvarga points
  if (savPoints !== null) {
    if (savPoints >= 32) score += 2;
    else if (savPoints >= 28) score += 1;
    else if (savPoints < 25) score -= 1.5;
  }

  // Verdict calculation
  let verdict: ComprehensiveHouseAnalysis['houseStrengthVerdict'];
  if (score >= 3.5) {
    verdict = {
      badge: 'Highly Auspicious & Powerful',
      color: 'emerald',
      summary: `House ${houseNumber} receives exceptional astrological backing through exalted or benefic influences and high vitality.`
    };
  } else if (score >= 1.0) {
    verdict = {
      badge: 'Strong & Favorable',
      color: 'teal',
      summary: `House ${houseNumber} is positively fortified with supportive lord placement and harmonious aspects.`
    };
  } else if (score >= -1.0) {
    verdict = {
      badge: 'Balanced & Steady',
      color: 'amber',
      summary: `House ${houseNumber} experiences a balanced mix of cosmic influences; manifests steady results over time.`
    };
  } else {
    verdict = {
      badge: 'Karmic Growth / Demands Remedy',
      color: 'rose',
      summary: `House ${houseNumber} encounters affliction or debilitation; challenges serve as catalysts for conscious inner growth and targeted Vedic remedies.`
    };
  }

  // Ashtakvarga Detailed Evaluation
  const sevenPlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  const bhinnaContributors = sevenPlanets.map((pName) => {
    const bindus = kundali.ashtakvarga?.bhinna?.[pName]?.[houseNumber - 1] ?? 0;
    const status: 'Strong' | 'Average' | 'Low' = bindus >= 5 ? 'Strong' : bindus === 4 ? 'Average' : 'Low';
    return {
      planet: pName,
      bindus,
      status
    };
  });

  const strongestGrahas = bhinnaContributors.filter((b) => b.bindus >= 5).map((b) => b.planet);
  const weakestGrahas = bhinnaContributors.filter((b) => b.bindus <= 3).map((b) => b.planet);

  let avRating: AshtakvargaHouseEvaluation['rating'] = 'Strong & Auspicious (28-31)';
  let avColor: AshtakvargaHouseEvaluation['statusColor'] = 'teal';
  let avAnalysis = '';
  let avTransit = '';

  const effectiveSav = savPoints ?? 28;

  if (effectiveSav >= 32) {
    avRating = 'Exceptional (32+)';
    avColor = 'emerald';
    avAnalysis = `With ${effectiveSav} Sarvashtakvarga bindus (well above the 28 benchmark), this house possesses supreme cosmic vitality. Matters of ${signification.englishTitle} experience strong natural cushion, material fulfillment, and rapid recovery from setbacks.`;
    avTransit = `Planetary transits over this sign—especially benefics like Jupiter and Venus—yield peak auspicious results, effortless opportunities, and milestone achievements.`;
  } else if (effectiveSav >= 28) {
    avRating = 'Strong & Auspicious (28-31)';
    avColor = 'teal';
    avAnalysis = `At ${effectiveSav} bindus, this house stands comfortably above the classical 28 baseline. It provides steady, fertile ground for all actions related to ${signification.englishTitle}.`;
    avTransit = `Planetary transits activate this house favorably; diligent efforts invested here materialize reliably into tangible success.`;
  } else if (effectiveSav >= 25) {
    avRating = 'Moderate (25-27)';
    avColor = 'amber';
    avAnalysis = `With ${effectiveSav} bindus (close to the 28 average), this house carries moderate strength. Outcomes in ${signification.englishTitle} depend directly on conscious discipline, personal drive, and supportive dasha periods.`;
    avTransit = `Difficult transits (like Saturn or Rahu) through this sign require mindful patience, careful organization, and realistic expectations.`;
  } else {
    avRating = 'Challenging (<25)';
    avColor = 'rose';
    avAnalysis = `At ${effectiveSav} bindus (below 25), this house has lower Ashtakvarga vitality. Matters of ${signification.englishTitle} may encounter delays, energy depletion, or friction if left unattended.`;
    avTransit = `Transits across this sign require proactive karmic care, strengthening the house lord (${signLord}), and avoiding impulsive risks during malefic periods.`;
  }

  const ashtakvargaEvaluation: AshtakvargaHouseEvaluation = {
    points: savPoints,
    benchmark: 28,
    rating: avRating,
    statusColor: avColor,
    analysis: avAnalysis,
    transitImplication: avTransit,
    bhinnaContributors,
    strongestGrahas,
    weakestGrahas
  };

  const gocharEvaluation = evaluateHouseGocharCondition(houseNumber, kundali, transitDate);

  return {
    houseNumber,
    sign: house.sign,
    signIndex: house.signIndex,
    signLord: house.signLord,
    signLordPosition,
    sarvashtakvargaPoints: savPoints,
    ashtakvargaEvaluation,
    gocharEvaluation,
    signification,
    occupants,
    aspects,
    parivartana,
    yogas,
    houseStrengthVerdict: verdict
  };
};

export const getGemstoneRecommendations = (chartData: KundaliChartData): { planet: string, stone: string, reason: string }[] => {
  const ascSign = ZODIAC_SIGNS[chartData?.ascendantIndex ?? 0];
  const ascLord = ZODIAC_LORDS[ascSign];
  
  const recs = [];
  
  // Basic recommendation logic based on ascendant lord and trine lords
  const gemstoneMap: Record<string, string> = {
    'Sun': 'Ruby',
    'Moon': 'Pearl',
    'Mars': 'Red Coral',
    'Mercury': 'Emerald',
    'Jupiter': 'Yellow Sapphire',
    'Venus': 'Diamond',
    'Saturn': 'Blue Sapphire'
  };

  // 1. Lagna Lord is usually good
  if (gemstoneMap[ascLord]) {
    recs.push({ planet: ascLord, stone: gemstoneMap[ascLord], reason: 'Lagna Lord (Foundation of self and vitality)' });
  }

  // 2. 5th and 9th Lord (Trine lords)
  const trineLords = [
    ZODIAC_LORDS[ZODIAC_SIGNS[(chartData.ascendantIndex + 4) % 12]],
    ZODIAC_LORDS[ZODIAC_SIGNS[(chartData.ascendantIndex + 8) % 12]]
  ];

  trineLords.forEach(lord => {
    if (lord !== ascLord && gemstoneMap[lord]) {
      recs.push({ planet: lord, stone: gemstoneMap[lord], reason: 'Trine Lord (Luck, intelligence and higher wisdom)' });
    }
  });

  // Filter out debilitated planets
  return recs.filter(r => {
    const p = chartData.planets.find(pl => pl.name === r.planet);
    return p ? p.dignity !== 'Debilitated' : true;
  });
};

export interface BusinessRecommendation {
  planet: string;
  industryEn: string;
  industryHi: string;
  examples: string[];
}

const PLANETARY_BUSINESS_MAP: Record<string, { industryEn: string; industryHi: string; examples: string[] }> = {
  'Sun': { industryEn: 'Government & Administration', industryHi: 'सरकारी एवं प्रशासनिक', examples: ['Government Contracts', 'Medicine & Pharmacy', 'Gold & Jewelry', 'Political Strategy', 'Administrative Roles'] },
  'Moon': { industryEn: 'Hospitality & Care', industryHi: 'आतिथ्य एवं सेवा', examples: ['Restaurants & Food', 'Dairy & Liquids', 'Travel & Tourism', 'Nursing/Care', 'Public Relations'] },
  'Mars': { industryEn: 'Engineering & Construction', industryHi: 'इंजीनियरिंग एवं निर्माण', examples: ['Real Estate', 'Construction', 'Defense/Security', 'Sports & Fitness', 'Mechanical Engineering'] },
  'Mercury': { industryEn: 'Commerce & Communication', industryHi: 'वाणिज्य एवं संचार', examples: ['E-commerce', 'IT & Software', 'Writing/Media', 'Trading', 'Analytics & Accounting'] },
  'Jupiter': { industryEn: 'Finance & Education', industryHi: 'वित्त एवं शिक्षा', examples: ['Consulting', 'Banking & Finance', 'Teaching & Coaching', 'Legal Advisory', 'Event Management'] },
  'Venus': { industryEn: 'Arts & Luxury', industryHi: 'कला एवं विलासिता', examples: ['Fashion & Apparel', 'Beauty & Cosmetics', 'Interior Design', 'Entertainment', 'Automobile/Luxury Vehicles'] },
  'Saturn': { industryEn: 'Manufacturing & Agriculture', industryHi: 'विनिर्माण एवं कृषि', examples: ['Heavy Industries', 'Agriculture', 'Mining & Oil', 'Labor Supply', 'Construction Materials'] },
  'Rahu': { industryEn: 'Disruptive Tech & Foreign Trade', industryHi: 'तकनीकी एवं विदेशी व्यापार', examples: ['Artificial Intelligence', 'Aviation', 'Import/Export', 'Photography/Cinematography', 'E-sports'] },
  'Ketu': { industryEn: 'Research & Healing', industryHi: 'अनुसंधान एवं चिकित्सा', examples: ['Alternative Medicine', 'Genetics', 'Occult/Astrology', 'Micro-electronics', 'Spiritual Retreats'] }
};

export function getBusinessRecommendations(occupants: string[], houseLord: string): BusinessRecommendation[] {
  const recommendations: BusinessRecommendation[] = [];
  const planetsToConsider = new Set([...occupants.filter(p => p !== 'Ascendant'), houseLord]);
  
  planetsToConsider.forEach(planet => {
    if (PLANETARY_BUSINESS_MAP[planet]) {
      recommendations.push({
        planet,
        ...PLANETARY_BUSINESS_MAP[planet]
      });
    }
  });

  return recommendations;
}
